import { PenIcon } from "@xola/icons";
import { getSymbol, Label, Switch, Table } from "@xola/ui-kit";
import { Loader } from "common";
import { isNil, round } from "lodash";
import React, { useState } from "react";
import {
    CatalogItemObject,
    Experience,
    ProductCatalogItem,
    PurchaseRule,
    PurchaseRuleShortcutType,
    UnitType,
} from "types";
import { useSeller } from "../../../hooks/useAccount";
import { useCollection } from "../../../hooks/useCollection";
import { useVisibility } from "../../../hooks/useVisibility";
import { isChoiceAddOn } from "../../../models/CatalogItem";
import { SecondaryButton } from "../../Common/SecondaryButton";
import { EditAddonPricingModal } from "./EditAddonPricingModal";
import {
    DiscountPricingValue,
    getCatalogByItem,
    getDefaultAction,
    getPurchaseAction,
    getShortcutType,
} from "./PricingSelectorHelper";

interface PackageExperienceAddOnDiscountListProps {
    purchaseRule?: PurchaseRule;
    experience: Experience;
    onChangeAddOnDiscount: (discountPricingValue: DiscountPricingValue[], isEnabled?: boolean) => void;
}

export const PackageExperienceAddOnDiscountList = ({
    purchaseRule,
    experience,
    onChangeAddOnDiscount,
}: PackageExperienceAddOnDiscountListProps) => {
    const [enableAddonDiscount, setEnableAddonDiscount] = useState(purchaseRule?.meta?.addons ?? true);
    const [visible, { toggle }] = useVisibility(false);
    const seller = useSeller();
    const symbol = getSymbol(seller.currency);

    const handleDiscountChange = (discountPricingValue: DiscountPricingValue[]) => {
        onChangeAddOnDiscount(discountPricingValue, enableAddonDiscount);
        toggle();
    };

    const handleDiscountToggle = (value: boolean) => {
        setEnableAddonDiscount(value);
        const discountPricingValue: DiscountPricingValue[] = [];

        if (!value) {
            for (const catalogAddOn of catalogAddOns) {
                if (isChoiceAddOn(catalogAddOn) && catalogAddOn.items) {
                    for (const choiceAddon of catalogAddOn.items) {
                        removePurchaseRuleAction(discountPricingValue, choiceAddon);
                    }
                } else {
                    removePurchaseRuleAction(discountPricingValue, catalogAddOn);
                }
            }
        }

        onChangeAddOnDiscount(discountPricingValue, value);
    };

    const removePurchaseRuleAction = (discountPricingValue: DiscountPricingValue[], addon: ProductCatalogItem) => {
        const purchaseRuleAction = getPurchaseAction(purchaseRule, addon.template?.id);
        if (purchaseRuleAction) {
            discountPricingValue.push({ action: { ...purchaseRuleAction, _remove: true } });
        }
    };

    const params = {
        product: experience.id,
        "unitType[in]": `${UnitType.ADDON},${UnitType.PICKUP}`,
        object: CatalogItemObject.PRODUCT_CATALOG_ITEM,
    };
    const { data: catalogAddOns, isLoading } = useCollection<ProductCatalogItem>("catalogItems", params);

    if (isLoading) {
        return <Loader />;
    }

    if (catalogAddOns.length === 0) {
        return null;
    }

    return (
        <div className="pb-6">
            <div className="flex items-center justify-between pb-1">
                <div className="mb-1 mt-4 flex items-center space-x-2">
                    <Switch isChecked={enableAddonDiscount} onChange={handleDiscountToggle} />
                    <Label>Add-On Discounts</Label>
                </div>
                {enableAddonDiscount && (
                    <SecondaryButton className="mb-1" size="tiny" onClick={toggle}>
                        <PenIcon />
                    </SecondaryButton>
                )}
                {enableAddonDiscount && (
                    <EditAddonPricingModal
                        isOpen={visible}
                        catalogAddOns={catalogAddOns}
                        purchaseRule={purchaseRule}
                        experience={experience}
                        onClose={toggle}
                        onChange={handleDiscountChange}
                    />
                )}
            </div>

            {enableAddonDiscount && (
                <Table>
                    <Table.Head>
                        <Table.Row>
                            <Table.Header className="!text-sm">Add-On</Table.Header>
                            <Table.Header className="!text-sm">Adjustment</Table.Header>
                        </Table.Row>
                    </Table.Head>

                    <Table.Body>
                        {catalogAddOns.map((addOn) => {
                            const purchaseAction = getPurchaseAction(purchaseRule, addOn.template?.id);
                            const action = purchaseAction ?? getDefaultAction();
                            const discount = Math.abs(round(action.shortcut?.amount ?? 0, 2));
                            const catalogItem = getCatalogByItem(catalogAddOns, addOn) ?? addOn;
                            const shortcutType = getShortcutType(action);
                            const choiceAddon = isChoiceAddOn(catalogItem);
                            const label =
                                shortcutType === PurchaseRuleShortcutType.PERCENT
                                    ? `${discount}% Off`
                                    : shortcutType === PurchaseRuleShortcutType.ABSOLUTE_FINAL_PRICE
                                    ? `${symbol}${discount}`
                                    : `${symbol}${discount} Off`;

                            return (
                                <Table.Row key={addOn.id} className="justify-between  border-b border-gray-light">
                                    <Table.Cell className="w-2/3">
                                        <span className="whitespace-normal break-all">{addOn.name}</span>
                                    </Table.Cell>
                                    <Table.Cell>{choiceAddon ? "Varies" : isNil(discount) ? null : label}</Table.Cell>
                                </Table.Row>
                            );
                        })}
                    </Table.Body>
                </Table>
            )}
        </div>
    );
};
